import z from "zod";

import type { JsonObject, JsonValue } from "./types";

import {
  type SourceControlDispatchContext,
  serializeSourceControlDispatchContext,
  sourceControlDispatchPayloadSchema,
} from "./sourceControl";
import { normalizeString } from "./stringUtils";

const normalizedStringSchema = z.preprocess(
  (value) => normalizeString(value),
  z.string().min(1),
);

const nullableNormalizedStringSchema = z.preprocess(
  (value) => normalizeString(value) ?? null,
  z.string().min(1).nullable(),
);

const taskDispatchTransportObjectSchema = z.object({}).catchall(z.unknown());
const jsonObjectSchema = z.preprocess(
  normalizeJsonObject,
  z.record(z.string(), z.json()),
);

export const taskCallbackTargetSchema = z.object({
  app_id: normalizedStringSchema,
  method: normalizedStringSchema,
});

export type TaskCallbackTarget = z.infer<typeof taskCallbackTargetSchema>;

export const taskDispatchTransportSchema = z.object({
  callback: z.unknown().nullable().optional().default(null),
  // Stage E attempt binding (item 4b). Optional so an old-orchestrator
  // dispatch admits cleanly; a node that receives it echoes it on every
  // callback for the run.
  dispatch_attempt_id: z.string().min(1).nullable().optional().default(null),
  metadata: z.preprocess(
    normalizeTransportObject,
    taskDispatchTransportObjectSchema,
  ),
  organization_id: z.string().min(1).nullable().optional().default(null),
  payload: z.preprocess(
    normalizeTransportObject,
    taskDispatchTransportObjectSchema,
  ),
  source_control: z.unknown().nullable().optional().default(null),
  source_control_provider_id: z
    .string()
    .min(1)
    .nullable()
    .optional()
    .default(null),
  task_id: z.string().min(1),
});

export const taskDispatchPayloadSchema = z.object({
  callback: z.preprocess(
    normalizeCallbackTarget,
    taskCallbackTargetSchema.nullable(),
  ),
  dispatch_attempt_id: nullableNormalizedStringSchema,
  metadata: jsonObjectSchema,
  organization_id: nullableNormalizedStringSchema,
  payload: jsonObjectSchema,
  source_control: z.preprocess(
    normalizeSourceControlDispatchPayload,
    sourceControlDispatchPayloadSchema.nullable(),
  ),
  source_control_provider_id: nullableNormalizedStringSchema,
  task_id: normalizedStringSchema,
});

export type TaskDispatchPayload = z.infer<typeof taskDispatchPayloadSchema>;

export function parseTaskDispatchPayloadWithSchemas<
  TPayloadSchema extends z.ZodType,
  TMetadataSchema extends z.ZodType,
>(
  value: unknown,
  input: { payloadSchema: TPayloadSchema; metadataSchema: TMetadataSchema },
):
  | (Omit<TaskDispatchPayload, "metadata" | "payload"> & {
      metadata: z.infer<TMetadataSchema>;
      payload: z.infer<TPayloadSchema>;
    })
  | null {
  const parsed = parseTaskDispatchPayload(value);
  if (!parsed) {
    return null;
  }

  const payloadResult = input.payloadSchema.safeParse(parsed.payload);
  if (!payloadResult.success) {
    return null;
  }

  const metadataResult = input.metadataSchema.safeParse(parsed.metadata);
  if (!metadataResult.success) {
    return null;
  }

  return {
    ...parsed,
    metadata: metadataResult.data,
    payload: payloadResult.data,
  };
}

export function serializeTaskDispatchPayload({
  taskId,
  payload,
  metadata,
  sourceControlProviderId,
  sourceControlContext,
  callback,
  dispatchAttemptId,
  organizationId,
}: {
  taskId: string;
  payload: Record<string, unknown>;
  metadata: Record<string, unknown>;
  sourceControlProviderId?: string | null;
  sourceControlContext?: SourceControlDispatchContext | null;
  callback?: { appId: string; method: string } | null;
  dispatchAttemptId?: string | null;
  organizationId?: string | null;
}): TaskDispatchPayload {
  return taskDispatchPayloadSchema.parse({
    callback: callback
      ? { app_id: callback.appId, method: callback.method }
      : null,
    dispatch_attempt_id: dispatchAttemptId ?? null,
    metadata,
    organization_id: organizationId ?? null,
    payload,
    source_control: sourceControlContext
      ? serializeSourceControlDispatchContext(sourceControlContext)
      : null,
    source_control_provider_id: sourceControlProviderId ?? null,
    task_id: taskId,
  });
}

export function parseTaskDispatchPayload(
  value: unknown,
): TaskDispatchPayload | null {
  const parsedTransport = taskDispatchTransportSchema.safeParse(value);
  if (!parsedTransport.success) {
    return null;
  }

  const parsedPayload = taskDispatchPayloadSchema.safeParse(
    parsedTransport.data,
  );
  return parsedPayload.success ? parsedPayload.data : null;
}

function normalizeCallbackTarget(value: unknown): TaskCallbackTarget | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const parsed = taskCallbackTargetSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function normalizeSourceControlDispatchPayload(value: unknown): unknown {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    return value;
  }

  const parsed = sourceControlDispatchPayloadSchema.safeParse(value);
  return parsed.success ? parsed.data : value;
}

function normalizeJsonObject(value: unknown): unknown {
  return isPlainObject(value) ? sanitizeJsonObject(value) : value;
}

function normalizeTransportObject(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }

  return Object.fromEntries(Object.entries(value));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function sanitizeJsonObject(value: Record<string, unknown>): JsonObject {
  return Object.fromEntries(
    Object.entries(value)
      .map(([key, entry]) => [key, sanitizeJsonValue(entry)] as const)
      .filter(
        (entry): entry is readonly [string, JsonValue] =>
          entry[1] !== undefined,
      ),
  );
}

function sanitizeJsonValue(value: unknown): JsonValue | undefined {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    const sanitized = value
      .map((entry) => sanitizeJsonValue(entry))
      .filter((entry): entry is JsonValue => entry !== undefined);
    return sanitized;
  }

  if (isPlainObject(value)) {
    return sanitizeJsonObject(value);
  }

  return undefined;
}
